// Rating tags shown in the add-comment flow, mirroring the iOS
// (`RatingTag.swift` / `RatingTagCatalog`) and Android (`RatingTag.kt`) apps.
//
// The `key` values are sent to the backend as `createComment(tagList:)` /
// `updateComment(tagList:)` and MUST stay in sync with the mobile enums — they
// are the canonical snake_case strings stored on the comment's tag list.

import { i18n } from '../../i18n';

const t = (key: string): string => i18n.global.t(key);

export interface RatingTagOption {
  key: string;
  label: string;
}

export interface RatingTagSection {
  id: 'quick' | 'conditions' | 'landscape' | 'features' | 'suitability';
  // `null` for the rating-specific "quick picks" block, which renders with no
  // header (matching the apps).
  title: string | null;
  tags: RatingTagOption[];
}

// key -> display label. Labels match the apps' English `rating_tag_*` strings.
// Each is resolved lazily so it follows the reader's locale.
const LABELS: Record<string, () => string> = {
  closed: () => t('utils.rating_tag.closed'),
  private_property: () => t('utils.rating_tag.private_property'),
  bad_condition: () => t('utils.rating_tag.bad_condition'),
  poor_condition: () => t('utils.rating_tag.poor_condition'),
  fair_condition: () => t('utils.rating_tag.fair_condition'),
  good_condition: () => t('utils.rating_tag.good_condition'),
  great_condition: () => t('utils.rating_tag.great_condition'),
  wrong_route: () => t('utils.rating_tag.wrong_route'),
  no_parking: () => t('utils.rating_tag.no_parking'),
  has_parking: () => t('utils.rating_tag.has_parking'),
  easy_to_park: () => t('utils.rating_tag.easy_to_park'),
  hard_to_park: () => t('utils.rating_tag.hard_to_park'),
  no_bathrooms: () => t('utils.rating_tag.no_bathrooms'),
  has_bathroom: () => t('utils.rating_tag.has_bathroom'),
  toilets_available: () => t('utils.rating_tag.toilets_available'),
  tide_dependant: () => t('utils.rating_tag.tide_dependant'),
  river_crossing: () => t('utils.rating_tag.river_crossing'),
  snowy: () => t('utils.rating_tag.snowy'),
  muddy: () => t('utils.rating_tag.muddy'),
  boggy: () => t('utils.rating_tag.boggy'),
  rocky: () => t('utils.rating_tag.rocky'),
  overgrown: () => t('utils.rating_tag.overgrown'),
  crowded: () => t('utils.rating_tag.crowded'),
  no_crowds: () => t('utils.rating_tag.no_crowds'),
  scenic_views: () => t('utils.rating_tag.scenic_views'),
  great_views: () => t('utils.rating_tag.great_views'),
  dog_friendly: () => t('utils.rating_tag.dog_friendly'),
  family_friendly: () => t('utils.rating_tag.family_friendly'),
  kid_friendly: () => t('utils.rating_tag.kid_friendly'),
  stroller_friendly: () => t('utils.rating_tag.stroller_friendly'),
  wheelchair_friendly: () => t('utils.rating_tag.wheelchair_friendly'),
  paved: () => t('utils.rating_tag.paved'),
  partially_paved: () => t('utils.rating_tag.partially_paved'),
  city_walk: () => t('utils.rating_tag.city_walk'),
  coastal_stroll: () => t('utils.rating_tag.coastal_stroll'),
  desert_path: () => t('utils.rating_tag.desert_path'),
  forest_bathing: () => t('utils.rating_tag.forest_bathing'),
  mountain_views: () => t('utils.rating_tag.mountain_views'),
  pub_walk: () => t('utils.rating_tag.pub_walk'),
  rail_trail: () => t('utils.rating_tag.rail_trail'),
  along_the_river: () => t('utils.rating_tag.along_the_river'),
  lake_views: () => t('utils.rating_tag.lake_views'),
  native_woods: () => t('utils.rating_tag.native_woods'),
  bridges_to_cross: () => t('utils.rating_tag.bridges_to_cross'),
  canyon_hike: () => t('utils.rating_tag.canyon_hike'),
  caves_to_explore: () => t('utils.rating_tag.caves_to_explore'),
  ancient_glaciers: () => t('utils.rating_tag.ancient_glaciers'),
  historical_trail: () => t('utils.rating_tag.historical_trail'),
  hot_springs: () => t('utils.rating_tag.hot_springs'),
  rock_formations: () => t('utils.rating_tag.rock_formations'),
  epic_photos: () => t('utils.rating_tag.epic_photos'),
  waterfalls_trail: () => t('utils.rating_tag.waterfalls_trail'),
  flowers_in_bloom: () => t('utils.rating_tag.flowers_in_bloom'),
  wildlife_area: () => t('utils.rating_tag.wildlife_area'),
  volcanic_features: () => t('utils.rating_tag.volcanic_features'),
  lookout_towers: () => t('utils.rating_tag.lookout_towers'),
  food: () => t('utils.rating_tag.food'),
  coffee: () => t('utils.rating_tag.coffee'),
};

// Rating-specific "quick picks" — the 6 tags shown immediately under the stars.
const QUICK_TAGS: Record<number, string[]> = {
  1: ['closed', 'private_property', 'bad_condition', 'wrong_route', 'no_parking', 'no_bathrooms'],
  2: ['no_parking', 'no_bathrooms', 'poor_condition', 'tide_dependant', 'boggy', 'overgrown'],
  3: ['no_bathrooms', 'crowded', 'hard_to_park', 'fair_condition', 'muddy', 'overgrown'],
  4: ['scenic_views', 'has_parking', 'good_condition', 'no_crowds', 'dog_friendly', 'has_bathroom'],
  5: ['great_views', 'easy_to_park', 'great_condition', 'no_crowds', 'dog_friendly', 'has_bathroom'],
};

// Expanded sections revealed by "More".
const LOW_STAR_CONDITIONS = [
  'tide_dependant', 'river_crossing', 'snowy', 'muddy', 'boggy', 'rocky', 'overgrown',
  'no_parking', 'no_bathrooms', 'closed', 'private_property', 'wrong_route', 'bad_condition', 'hard_to_park',
];

const HIGH_STAR_LANDSCAPE = [
  'city_walk', 'coastal_stroll', 'desert_path', 'forest_bathing', 'mountain_views',
  'pub_walk', 'rail_trail', 'along_the_river', 'lake_views', 'native_woods',
];

const HIGH_STAR_FEATURES = [
  'bridges_to_cross', 'canyon_hike', 'caves_to_explore', 'ancient_glaciers', 'historical_trail',
  'hot_springs', 'rock_formations', 'epic_photos', 'waterfalls_trail', 'flowers_in_bloom',
  'wildlife_area', 'volcanic_features', 'lookout_towers', 'scenic_views',
];

const HIGH_STAR_SUITABILITY = [
  'dog_friendly', 'family_friendly', 'kid_friendly', 'stroller_friendly', 'wheelchair_friendly',
  'paved', 'partially_paved', 'toilets_available',
];

const HIGH_STAR_CONDITIONS = [
  'has_parking', 'food', 'coffee', 'has_bathroom', 'fair_condition', 'good_condition', 'great_condition',
];

export const ratingTagLabel = (key: string): string =>
  LABELS[key]?.() || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const toOptions = (keys: string[]): RatingTagOption[] =>
  keys.map((key) => ({ key, label: ratingTagLabel(key) }));

// Ordered sections shown on the rating-tags step for the given star value.
// Sections render top-to-bottom; the first is always the rating-specific
// "quick picks" with no header. Mirrors `RatingTagCatalog.sections(for:)`.
export const ratingTagSections = (rating: number): RatingTagSection[] => {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) return [];

  const quick: RatingTagSection = { id: 'quick', title: null, tags: toOptions(QUICK_TAGS[rating]) };

  if (rating <= 3) {
    return [
      quick,
      { id: 'conditions', title: t('utils.rating_tag.conditions'), tags: toOptions(LOW_STAR_CONDITIONS) },
    ];
  }

  return [
    quick,
    { id: 'landscape', title: t('utils.rating_tag.landscape'), tags: toOptions(HIGH_STAR_LANDSCAPE) },
    { id: 'features', title: t('utils.rating_tag.features'), tags: toOptions(HIGH_STAR_FEATURES) },
    { id: 'suitability', title: t('utils.rating_tag.suitability'), tags: toOptions(HIGH_STAR_SUITABILITY) },
    { id: 'conditions', title: t('utils.rating_tag.conditions'), tags: toOptions(HIGH_STAR_CONDITIONS) },
  ];
};
